import { ReactNode } from "react";

import { ArrowLeft, Loader2, AlertCircle, RefreshCw } from "lucide-react";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button-shadcn";

import {
  FILE_DETAIL_RAIL_HEADER_ROW_CLS,
  FILE_DETAIL_RAIL_HEADER_TITLE_CLS,
} from "../../fileDetailLayout";
import { ExtractionResults, ExtractionType, EXTRACTION_OPERATIONS } from "./types";

interface ExtractionResultsViewProps {
  type: ExtractionType;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onRetry: () => void;
  onReExtract?: () => void;
  resultCount?: number;
  loadingPhase?: "analyzing" | "extracting" | null;
  /** Previous results to keep visible while a re-extraction is in progress. */
  previousResults?: ExtractionResults | null;
  children: ReactNode;
}

const LOADING_MESSAGES: Record<ExtractionType, string> = {
  dates: "Extracting dates from document...",
  summary: "Generating document summary...",
  checklist: "Extracting checklists from document...",
  location: "Finding locations in document...",
  budget: "Extracting budget information...",
  client: "Finding clients and organizations...",
  notes: "Extracting notes from document...",
};

const EMPTY_MESSAGES: Record<ExtractionType, string> = {
  dates: "No dates found in this document.",
  summary: "Could not generate a summary for this document.",
  checklist: "No checklists found in this document.",
  location: "No locations found in this document.",
  budget: "No budget information found in this document.",
  client: "No clients or organizations found in this document.",
  notes: "No notes found in this document.",
};

const ANALYZING_MESSAGE = "Analyzing document...";

const CENTERED_STATE_CLS =
  "flex min-h-full flex-col items-center justify-center gap-3 px-4 py-10 text-center";

export const ExtractionResultsView = ({
  type,
  loading,
  error,
  onBack,
  onRetry,
  onReExtract,
  resultCount,
  loadingPhase,
  previousResults,
  children,
}: ExtractionResultsViewProps) => {
  const operation = EXTRACTION_OPERATIONS.find((op) => op.type === type);
  const label = operation?.label || "Extraction";

  // When re-extracting (loading=true but we have previousResults), show old content
  // with an inline progress bar rather than wiping the view.
  const isReExtracting = loading && !!previousResults;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className={FILE_DETAIL_RAIL_HEADER_ROW_CLS}>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-9 shrink-0 rounded-full"
          onClick={onBack}
          aria-label="Back to operations"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h3 className={FILE_DETAIL_RAIL_HEADER_TITLE_CLS}>
          {label}
          {resultCount !== undefined && resultCount > 0 && (
            <span className="font-normal text-muted-foreground"> ({resultCount})</span>
          )}
        </h3>
        {isReExtracting && (
          <Loader2 className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />
        )}
        {onReExtract && !loading && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-9 shrink-0 rounded-full"
            onClick={onReExtract}
            aria-label="Re-extract"
            title="Re-extract from document"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Indeterminate progress bar shown during re-extraction (preserves old content below) */}
      {isReExtracting && (
        <div className="relative h-0.5 w-full bg-primary/10 shrink-0 overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1/2 bg-primary animate-shimmer" />
        </div>
      )}

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* First extraction (no previous results) — full loading state */}
        {loading && !previousResults && (
          <div className={CENTERED_STATE_CLS}>
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">
              {loadingPhase === "analyzing" ? ANALYZING_MESSAGE : LOADING_MESSAGES[type]}
            </p>
          </div>
        )}

        {error && !loading && (
          <div className={CENTERED_STATE_CLS}>
            <AlertCircle className="w-6 h-6 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && resultCount === 0 && (
          <div className={CENTERED_STATE_CLS}>
            <p className="text-sm text-muted-foreground">
              {EMPTY_MESSAGES[type]}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Try Again
            </Button>
          </div>
        )}

        {/* Show results when done, or stale content while re-extracting */}
        {((!loading && !error && (resultCount === undefined || resultCount > 0)) || isReExtracting) && (
          <div className={cn(isReExtracting && "opacity-60 pointer-events-none transition-opacity duration-150")}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
